import { mapUtil } from '@sw-sw/common';
import PropTypes from 'prop-types';
import React, { Component, Fragment, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AppDivisionContext from '../../../contexts/AppDivisionContext';
import { ProjectContext } from '../../../contexts/ProjectContext';
import ProjectPermissionContext from '../../../contexts/ProjectPermissionContext';
import uploadApi from '../../../utils/api/upload';
import SiteMapCard from '../../Card/SiteMapCard';
import ConfirmationModal from '../../Shared/ConfirmationModal/ConfirmationModal';
import SuccessModal from '../../Shared/SuccessModal/SuccessModal';
import SiteMapPrint from '../../Inspections/Details/SiteMapPrint';
import PrintSiteMapsModal from '../Header/PrintSiteMapsModal';
import { ProjectPrintProvider } from '../../../contexts/ProjectPrintContext';
import AppContext from '../../../contexts/AppContext';
import _ from 'lodash';
import ResourceSearch from '../../Shared/Resource/Search';
import useResource from '../../../hooks/resource';
import UploadInput from '../../FormInput/Upload';
import { RolesContext } from '../../../contexts/RolesContext';
import FileRenameModal from '../../Shared/FileRow/FileRenameModal';
import { Modal } from '@sw-sw/lib-ui';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import projectApi from '../../../utils/api/project';
import projectInspectionTemplatesApi from '../../../utils/api/projectInspectionTemplates';
import TenantFeatureContext from '../../../contexts/TenantContext';

const { isSupportedMimeType } = mapUtil;
/**
 * Render cards for each site map in a project
 *
 * @todo optional canEdit
 * @todo generic 'map editor' route, at root of app, instead of route-per-map
 */
class SiteMapsUI extends Component {
  fileInputRef = React.createRef();
  uploadInputRef = React.createRef();
  hiddenFileInputRef = React.createRef();
  state = {
    selected: null,
    showConfirmation: false,
    showArchiveConfirmation: false,
    showRestoreConfirmation: false,
    showPrintConfirmation: false,
    showRenameModal: false,
    showReplaceModal: false,
    showTemplateLinkModal: false,
    showAspectRatioWarning: false,
    targetMap: null,
    error: '',
    targetDoc: {},
    archiveView: false,
    isLoading: false,
    mapTypeFilter: null, // Will be set to first docType's inspection_template_type_id
    selectedTemplateType: null,
    selectedMapType: 'image',
    imageOverlay: false,
    esriLayers: false,
    esriUrl: '',
    esriChecking: false,
    esriVerified: false,
    esriError: null,
    inspectionTemplates: [],
    loadingTemplates: false,
    aspectWarningMessage: '',
    pendingReplaceData: null,
  };

  static contextType = AppDivisionContext;


  get editorProps() {
    const { selected } = this.state;

    const editorProps = {
      onClose: () => this.deselect(),
    };

    if (selected !== null) {
      Object.assign(editorProps, {
        map: selected,
        title: this.props.projectName,
        subtitle: selected.name,
      });
    }

    return editorProps;
  }

  handleCardClick = (selected, isPublic) => {
    this.setState({ selected }, () => {
      const { history, projectId, inspectionId, isActiveMap } = this.props;
      const ext = isActiveMap ? 'active-map' : 'inspection';
      const isPending = selected.ProjectUploadModel?.upload_id == null;
      const mapToken = isPending ? 'guid-pending' : selected.GUID;
      const pendingQuery = isPending ? `&projectUploadId=${selected.ProjectUploadModel.id}` : '';
      const archiveQuery = this.state.archiveView ? '&archive' : '';
      const path = `/projects/${projectId}/map/${mapToken}/edit?from=${ext}${archiveQuery}${pendingQuery}`;

      if (isPublic) {
        history.push(this.context.getPublicPath(path), { inspectionId });
      } else {
        history.push(this.context.getPath(path), { inspectionId });
      }
    });
  };

  deselect = () => {
    this.setState({ selected: null }, () => {
      const { history, match } = this.props;

      history.push(this.context.getPath(match.url));
    });
  };

  handleClick = () => {
    this.setState({ archiveView: !this.state.archiveView });
  };

  handleOrganizeClick = () => {
    if (!this.state.archiveView) {
      this.setState({
        showReorderModal: true,
        tempReorderProjectDocs: this.props.siteMaps,
      });
    } else {
      this.setState({
        showReorderModal: true,
        tempReorderProjectDocs: this.props.archivesiteMaps,
      });
    }
  };

  computeSiteMapType = () => {
    const mapType = this.state.selectedMapType || 'image';

    if (mapType !== 'gis') return 'image';
    if (this.state.imageOverlay && this.state.esriLayers) return 'gis_image_esri';
    if (this.state.imageOverlay) return 'gis_image';
    if (this.state.esriLayers) return 'gis_esri';

    return 'gis_only';
  };

  // CHECK URL — validates the pasted ArcGIS URL via backend. Continue stays disabled until this passes.
  handleEsriCheckUrl = async () => {
    const url = (this.state.esriUrl || '').trim();
    if (!url) {
      this.setState({ esriError: 'Please paste a layer URL.' });
      return;
    }
    this.setState({ esriChecking: true, esriError: null });
    try {
      const result = await projectApi.esriLayer.check(this.props.projectId, url);
      if (result && result.valid) {
        this.setState({ esriVerified: true, esriError: null });
      } else {
        this.setState({
          esriVerified: false,
          esriError: (result && result.error) || 'URL could not be validated.',
        });
      }
    } catch (err) {
      this.setState({
        esriVerified: false,
        esriError: 'Validation request failed. Try again.',
      });
    } finally {
      this.setState({ esriChecking: false });
    }
  };

  resetEsriModalState = () => ({
    esriLayers: false,
    esriUrl: '',
    esriChecking: false,
    esriVerified: false,
    esriError: null,
  });

  handleUploadButtonClick = async () => {
    // If accessed via /map (inspection context), skip modal and trigger upload directly
    if (this.props.inspectionId && this.props.inspectionTemplateTypeId) {
      // Directly trigger the file input without showing modal
      setTimeout(() => {
        if (this.uploadInputRef.current) {
          const inputElement = this.uploadInputRef.current.querySelector('input[type="file"]');

          if (inputElement) {
            inputElement.click();
          }
        }
      }, 100);
      return;
    }

    // If accessed via /active-site-map, show modal to select template
    this.setState({ loadingTemplates: true, showTemplateLinkModal: true });

    try {
      const response = await projectInspectionTemplatesApi.index(this.props.projectId);

      const primaryTemplate = (response.data || []).find(template => template.is_primary);
      if (primaryTemplate && primaryTemplate.inspection_template) {
        this.setState({ selectedTemplateType: primaryTemplate.inspection_template.inspection_type_id });
      }

      // Make the array unique based on inspection_type_id
      const uniqueTemplates = [];
      const seenTypeIds = new Set();

      (response.data || []).forEach((template) => {
        const typeId = template.inspection_template.inspection_type_id;

        if (!seenTypeIds.has(typeId)) {
          seenTypeIds.add(typeId);
          uniqueTemplates.push(template);
        }
      });

      this.setState({
        inspectionTemplates: uniqueTemplates,
        loadingTemplates: false,
      });
    } catch (error) {
      console.error('Error fetching inspection templates:', error);
      this.setState({
        loadingTemplates: false,
        error: 'Failed to load inspection templates',
      });
    }
  };

  handleTemplateLinkConfirm = async () => {
    const siteMapType = this.computeSiteMapType();
    const inspectionTypeId = this.state.selectedTemplateType;

    // GIS Mode (Live Map): no image is uploaded yet, regardless of which
    // overlay checkboxes are toggled. Create a pending project_upload row and
    // jump straight into the configure view; the screenshot is captured there.
    if (this.state.selectedMapType === 'gis' && !this.state.imageOverlay) {
      try {
        const hasNoMaps =
          !this.props.siteMaps || this.props.siteMaps.length === 0;
        let projectDocTypeId;

        if (hasNoMaps) {
          const createDocTypeResponse = await projectApi.createDocType({
            project_id: this.props.projectId,
            inspection_template_type_id: inspectionTypeId,
            doc_type_id: 1,
          });

          if (!createDocTypeResponse?.data?.id) {
            this.setState({ error: 'Failed to create document type' });
            return;
          }
          projectDocTypeId = createDocTypeResponse.data.id;
        } else {
          const response = await projectApi.getDocTypeByInspectionTemplateType(
            this.props.projectId,
            inspectionTypeId,
          );

          if (!response?.project_doc_type_id) {
            this.setState({
              error: 'Failed to fetch document type from API',
            });
            return;
          }
          projectDocTypeId = response.project_doc_type_id;
        }

        const pending = await projectApi.createPendingSiteMap(
          this.props.projectId,
          {
            project_doc_type_id: projectDocTypeId,
            site_map_type: siteMapType,
            esri_url: this.state.esriLayers ? this.state.esriUrl.trim() : null,
          },
        );

        this.setState({
          showTemplateLinkModal: false,
          selectedTemplateType: null,
          selectedMapType: 'image',
          imageOverlay: false,
          ...this.resetEsriModalState(),
        });

        this.props.history.push(
          this.context.getPath(
            `/projects/${this.props.projectId}/map/guid-pending/edit?from=active-map&projectUploadId=${pending.id}`,
          ),
        );
        return;
      } catch (err) {
        console.error('Failed to create pending GIS site map', err);
        this.setState({
          error: 'Failed to start GIS configuration. Please try again.',
        });
        return;
      }
    }

    // All other types: existing flow — close modal, trigger the file picker;
    // handleUploadClick will create the upload + project_upload after the file is chosen.
    this.setState({ showTemplateLinkModal: false }, () => {
      setTimeout(() => {
        if (this.uploadInputRef.current) {
          const inputElement = this.uploadInputRef.current.querySelector('input[type="file"]');
          if (inputElement) inputElement.click();
        }
      }, 100);
    });
  };

  handleUploadClick = async (newFile, templateTypeId) => {
    try {
      // Priority order for determining inspection type ID:
      // 1. templateTypeId from Upload component (auto-detected from context)
      // 2. inspectionTemplateTypeId from props (when in inspection context)
      // 3. selectedTemplateType from modal selection
      const inspectionTypeId = templateTypeId || this.props.inspectionTemplateTypeId || this.state.selectedTemplateType;

      // Check if there are no maps - if so, create the doc type first
      const hasNoMaps = !this.props.siteMaps || this.props.siteMaps.length === 0;

      let projectDocTypeId;

      if (hasNoMaps) {
        // Create the doc type first since it doesn't exist
        // doc_type_id: 1 is for "Active Site Maps"
        const createDocTypeResponse = await projectApi.createDocType({
          project_id: this.props.projectId,
          inspection_template_type_id: inspectionTypeId,
          doc_type_id: 1
        });

        if (!createDocTypeResponse || !createDocTypeResponse.data || !createDocTypeResponse.data.id) {
          this.setState({
            error: 'Failed to create document type',
          });
          return;
        }

        projectDocTypeId = createDocTypeResponse.data.id;
      } else {
        // Fetch the existing document type
        const response = await projectApi.getDocTypeByInspectionTemplateType(
          this.props.projectId,
          inspectionTypeId
        );

        if (!response || !response.project_doc_type_id) {
          this.setState({
            error: 'Failed to fetch document type from API',
          });
          return;
        }

        projectDocTypeId = response.project_doc_type_id;
      }

      // Use the project_doc_type_id to upload the file
      await uploadApi.project.create(
        newFile.id,
        this.props.projectId,
        projectDocTypeId,
        null,
        {
          site_map_type: this.computeSiteMapType(),
          esri_url: this.state.esriLayers ? this.state.esriUrl.trim() : null,
        },
      );

      // Reset modal selections
      this.setState({
        selectedTemplateType: null,
        selectedMapType: 'image',
        imageOverlay: false,
        ...this.resetEsriModalState(),
      });

      return this.props.onDocumentUpload();
    } catch (error) {
      console.error('Error in handleUploadClick:', error);
      this.setState({
        error: 'Failed to upload document. Please try again.',
      });
    }
  };

  handleRename = (fileName) => {
    uploadApi.rename(this.state.targetDoc.GUID, fileName)
    this.setState({ showRenameModal: false })

    return this.props.onDocumentUpload()
  }

  handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      this.handleReplace(file)
    }
  };

  handleReplace = async (file) => {
    this.setState({ isLoading: true });
    uploadApi.create(file)
      .then(async (resposnse) => {
        const isAspectCorrect = await uploadApi.project.checkAspectRatioChange(
          this.props.projectId,
          {
            oldDocId: this.state.targetDoc.ProjectUploadModel.id,
            newDocId: resposnse.id,
          }
        );

        if (isAspectCorrect.requiresConfirmation) {
          // Show warning modal and store pending data
          this.setState({
            isLoading: false,
            showAspectRatioWarning: true,
            aspectWarningMessage: isAspectCorrect.aspectWarning || 'The aspect ratio of the new image differs from the existing one. This may affect annotations and measurements. Do you want to proceed?',
            pendingReplaceData: {
              response: resposnse,
              projectDocTypeId: this.state.targetDoc.ProjectUploadModel?.project_doc_type_id || this.state.targetDoc.project_doc_type_id,
            },
          });
          return;
        }

        // If no confirmation needed, proceed directly
        const projectDocTypeId = this.state.targetDoc.ProjectUploadModel?.project_doc_type_id || this.state.targetDoc.project_doc_type_id;

        await uploadApi.project.create(resposnse.id, this.props.projectId, projectDocTypeId);

        let payload = {
          newDoc: {
            ...resposnse,
            project_doc_type_id: projectDocTypeId,
            project_id: this.props.projectId,
          },
          oldDoc: this.state.targetDoc,
        };

        await uploadApi.project.replaceDocData(this.props.projectId, payload);
        this.setState({ isLoading: false });

        return this.props.onDocumentUpload();
      })
      .catch((error) => {
        console.error('Error in handleReplace:', error);
        this.setState({
          isLoading: false,
          error: 'Failed to replace document. Please try again.',
        });
      });
  }

  handleAspectRatioConfirm = async () => {
    const { pendingReplaceData } = this.state;


    if (!pendingReplaceData) return;

    this.setState({
      isLoading: true,
      showAspectRatioWarning: false,
    });

    try {
      const { response, projectDocTypeId } = pendingReplaceData;

      await uploadApi.project.create(response.id, this.props.projectId, projectDocTypeId);

      let payload = {
        newDoc: {
          ...response,
          project_doc_type_id: projectDocTypeId,
          project_id: this.props.projectId,
        },
        oldDoc: this.state.targetDoc,
      };

      await uploadApi.project.replaceDocData(this.props.projectId, payload);

      this.setState({
        isLoading: false,
        pendingReplaceData: null,
        aspectWarningMessage: '',
      });


      return this.props.onDocumentUpload();
    } catch (error) {
      console.error('Error in handleAspectRatioConfirm:', error);
      this.setState({
        isLoading: false,
        pendingReplaceData: null,
        aspectWarningMessage: '',
        error: 'Failed to replace document. Please try again.',
      });
    }
  }

  handleArchive = () => {
    uploadApi.project
      .archive(this.props.projectId, this.state.targetDoc.id, this.state.archiveView ? "Restore" : "Archive")
      .then(() => {
        if (this.state.archiveView) {
          const ele = this.props.archivesiteMaps.filter((arr) => arr.GUID === this.state.targetDoc.GUID)
          const index = this.props.archivesiteMaps.indexOf(ele[0])

          this.props.archivesiteMaps.splice(index, 1)

          this.props.setSiteMaps([...this.props.siteMaps, ele[0]])
          this.props.setArchiveSiteMaps(this.props.archivesiteMaps)
        } else {
          const ele = this.props.siteMaps.filter((arr) => arr.GUID === this.state.targetDoc.GUID)
          const index = this.props.siteMaps.indexOf(ele[0])

          this.props.siteMaps.splice(index, 1)

          this.props.setArchiveSiteMaps([...this.props.archivesiteMaps, ele[0]])
          this.props.setSiteMaps(this.props.siteMaps)
        }
        this.setState({ showArchiveConfirmation: false, showRestoreConfirmation: false });
      })
      .catch((err) =>
        this.setState({
          error:
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'An error has occurred',
        }),
      );
  };

  handleDelete = () => {
    uploadApi.project
      .destroy(this.props.projectId, this.state.targetDoc.id)
      .then(() => {
        if (this.state.archiveView) {
          const ele = this.props.archivesiteMaps.filter((arr) => arr.GUID === this.state.targetDoc.GUID)
          const index = this.props.archivesiteMaps.indexOf(ele[0])

          this.props.archivesiteMaps.splice(index, 1)
          this.props.setArchiveSiteMaps(this.props.archivesiteMaps)
        }
        else {
          const ele = this.props.siteMaps.filter((arr) => arr.GUID === this.state.targetDoc.GUID)
          const index = this.props.siteMaps.indexOf(ele[0])

          this.props.siteMaps.splice(index, 1)
          this.props.setSiteMaps(this.props.siteMaps)
        }
        this.props.onDocumentDelete(this.state.targetDoc.GUID);
        this.setState({ showConfirmation: false });
      })
      .catch((err) =>
        this.setState({
          error:
            err.response && err.response.data && err.response.data.message
              ? err.response.data.message
              : 'An error has occurred',
        }),
      );
  };

  reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);

    result.splice(endIndex, 0, removed);

    return result;
  };

  handleSubmitReorderProjectDocs = async () => {
    await projectApi.sortDocs(this.state.tempReorderProjectDocs);
  };

  handleOnDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const reordered = this.reorder(
      this.state.tempReorderProjectDocs,
      result.source.index,
      result.destination.index,
    );

    this.setState({ tempReorderProjectDocs: reordered });
  };

  getImgSrc = _.memoize((doc) => {
    return uploadApi.getDownloadUrl(doc.GUID, 450);
  });

  render() {
    const isPublic = this.props.roleName === 'Public' || this.props.roleName === "Public No Image";

    return (
      <Fragment>
        <input
          type='file'
          ref={this.fileInputRef}
          style={{ display: 'none' }}
          onChange={this.handleFileChange}
          accept='.jpg,.jpeg,.png'
        />
        {(this.props.siteMaps && this.props.siteMaps.length) ||
          (this.props.archivesiteMaps && this.props.archivesiteMaps.length) ? (
          <>
            {!(this.props.siteMaps && this.props.siteMaps.length) &&
              !this.state.archiveView && (
                <p>No active site maps have been uploaded.</p>
              )}
            <div className='project-site-maps pure-g pure-g-with-gutters'>
              <div
                style={{
                  display: 'flex',
                  gap: '5px',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                }}
              >
                <ResourceSearch
                  local={false}
                  {...this.props.searchProps}
                  style={{ flex: 2, minWidth: '285px' }}
                />
                {!this.props.inspectionId && this.props.docTypes && this.props.docTypes.length > 0 && (
                  <select
                    className='pure-button button-outline-tertiary'
                    style={{ width: '33%', minWidth: '120px', flex: 1, padding: '8px' }}
                    value={this.state.mapTypeFilter || 'all'}
                    onChange={(e) => this.setState({ mapTypeFilter: e.target.value })}
                  >
                    <option value="all">All</option>
                    {this.props.docTypes.map((docType) => (
                      <option
                        key={docType.inspection_template_type_id}
                        value={String(docType.inspection_template_type_id)}
                      >
                        {docType.inspection_template_type || 'Unknown Template'}
                      </option>
                    ))}
                  </select>
                )}
                {this.props.permCheck('create', 'Uploads') && navigator.onLine &&
                  <button
                    className='card-action pure-button button-outline-tertiary'
                    style={{ width: '33%', minWidth: '120px', flex: 1 }}
                    onClick={this.handleOrganizeClick}
                  >
                    Organize
                  </button>
                }
                {navigator.onLine &&
                  <button
                    className='card-action pure-button button-outline-tertiary'
                    style={{ width: '33%', minWidth: '120px', flex: 1 }}
                    onClick={this.handleClick}
                  >
                    {this.state.archiveView
                      ? 'Active Site Maps'
                      : 'Archived Maps'}
                  </button>
                }
                {!this.state.archiveView && navigator.onLine &&
                  this.props.permCheck('create', 'Uploads') &&
                  this.props.permCheck('update', 'Project Documents') && (
                    <>
                      <button
                        className='pure-button button-primary'
                        style={{ width: '37px', height: '37px', margin: '3px', padding: '0' }}
                        onClick={this.handleUploadButtonClick}
                      >
                        <i className="fa fa-cloud-upload" />
                      </button>
                      <div style={{ display: 'none' }} ref={this.uploadInputRef}>
                      <UploadInput
                      onUpload={(newFile, templateTypeId) => this.handleUploadClick(newFile, templateTypeId)}
                      allowedTypes={['image/jpg', 'image/jpeg', 'image/png']}
                      maxFileSize={75000000}
                      imageDimensions={{
                      width: 1000,
                      height: 1000,
                      }}
                      projectId={this.props.projectId}
                      autoSelectPrimaryTemplate={!this.props.siteMaps || this.props.siteMaps.length === 0}
                      inspectionId={this.props.inspectionId}
                      inspectionTemplateTypeId={this.props.inspectionTemplateTypeId}
                      showHelpText
                      />
                      </div>
                    </>
                  )}
              </div>
              <div className='map-container'>
                {this.state.archiveView
                  ? this.props.archivesiteMaps
                    .filter((e) => {
                      // Only filter by inspection_template_type_id when NOT accessed via inspection
                      if (!this.props.inspectionId) {
                        const filterValue = this.state.mapTypeFilter || 'all';

                        if (filterValue !== 'all' && e.inspection_template_type_id !== Number(filterValue)) {
                          return false;
                        }
                      }

                      // Filter by search value
                      if (this.props.searchValue) {
                        const mapName = e.name.toLowerCase();
                        const searchValue =
                          this.props.searchValue.query.toLowerCase();

                        return mapName.match(searchValue);
                      }

                      return true;
                    })
                    .map((doc) => (
                      <div className='pure-u-1 pure-u-md-1-3' key={doc.GUID}>
                        <SiteMapCard
                          imgSrc={this.getImgSrc(doc)}
                          title={doc.name}
                          label='Project'
                          handleImgClick={() =>
                            this.handleCardClick(doc, isPublic)
                          }
                          handleDelete={() =>
                            this.setState({
                              showConfirmation: true,
                              targetDoc: doc,
                            })
                          }
                          handleArchive={() =>
                            this.setState({
                              showRestoreConfirmation: true,
                              targetDoc: doc,
                            })
                          }
                          handleRename={() =>
                            this.setState({
                              showRenameModal: true,
                              targetDoc: doc,
                            })
                          }
                          handleReplace={() => {
                            this.setState({
                              targetDoc: doc,
                            });
                            this.fileInputRef.current.click();
                          }}
                          handlePrint={() =>
                            this.setState({
                              showPrintConfirmation: true,
                              targetDoc: doc,
                            })
                          }
                          handleDownload={() => {
                            const url = uploadApi.getDownloadUrl(doc.GUID);

                            window.open(url, '_blank');
                          }}
                          canArchive={
                            this.props.projectPermissionContext.readOnly
                              ? false
                              : this.props.permCheck('all', 'Restore', this.props.isDownloaded())
                          }
                          isArchive={true}
                          isLoading={
                            doc === this.state.targetDoc &&
                            this.state.isLoading
                          }
                          isDownloaded={this.props.isDownloaded()}
                          isPending={doc.ProjectUploadModel?.upload_id == null}
                          siteMapType={doc.ProjectUploadModel?.site_map_type}
                        />
                      </div>
                    ))
                  : this.props.siteMaps
                    .filter((e) => {
                      // Only filter by inspection_template_type_id when NOT accessed via inspection
                      if (!this.props.inspectionId) {
                        const filterValue = this.state.mapTypeFilter || 'all';

                        if (filterValue !== 'all' && e.inspection_template_type_id !== Number(filterValue)) {
                          return false;
                        }
                      }

                      // Filter by search value
                      if (this.props.searchValue) {
                        const mapName = e.name.toLowerCase();
                        const searchValue =
                          this.props.searchValue.query.toLowerCase();

                        return mapName.match(searchValue);
                      }

                      return true;
                    })
                    .map((doc) => (
                      <div className='pure-u-1 pure-u-md-1-3' key={doc.GUID}>
                        <SiteMapCard
                          imgSrc={this.getImgSrc(doc)}
                          title={doc.name}
                          label='Project'
                          handleImgClick={() =>
                            this.handleCardClick(doc, isPublic)
                          }
                          handleDelete={() =>
                            this.setState({
                              showConfirmation: true,
                              targetDoc: doc,
                            })
                          }
                          handleArchive={() =>
                            this.setState({
                              showArchiveConfirmation: true,
                              targetDoc: doc,
                            })
                          }
                          handleRename={() =>
                            this.setState({
                              showRenameModal: true,
                              targetDoc: doc,
                            })
                          }
                          handleReplace={() => {
                            this.setState({
                              targetDoc: doc,
                            });
                            this.fileInputRef.current.click();
                          }}
                          handlePrint={() =>
                            this.setState({
                              showPrintConfirmation: true,
                              targetDoc: doc,
                            })
                          }
                          handleDownload={() => {
                            const url = uploadApi.getDownloadUrl(doc.GUID);

                            window.open(url, '_blank');
                          }}
                          canArchive={
                            this.props.projectPermissionContext.readOnly
                              ? false
                              : this.props.permCheck('all', 'Archive', this.props.isDownloaded()) && navigator.onLine
                          }
                          isLoading={
                            doc === this.state.targetDoc &&
                            this.state.isLoading
                          }
                          isDownloaded={this.props.isDownloaded()}
                          isPending={doc.ProjectUploadModel?.upload_id == null}
                          siteMapType={doc.ProjectUploadModel?.site_map_type}
                        />
                      </div>
                    ))}
              </div>
            </div>
          </>
        ) : isPublic ? (
          <p>No site maps have been uploaded.</p>
        ) : (
          <>
            <p>
              No active site maps have been uploaded. Click the button below to
              upload a site map.
            </p>
            {this.props.permCheck('create', 'Uploads') &&
              this.props.permCheck('update', 'Project Documents') &&
              navigator.onLine && (
                this.props.primaryMappingMode === 'gis' && this.props.isGISEnabled && !this.props.inspectionId ? (
                  <>
                    <div
                      className='upload-site-maps-button'
                      onClick={this.handleUploadButtonClick}
                      role='button'
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') this.handleUploadButtonClick(); }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className='upload-control dropzone'>
                        <i className='fa fa-cloud-upload upload-control__icon' />
                        <span>Click to upload a site map.</span>
                      </div>
                    </div>
                    <div style={{ display: 'none' }} ref={this.uploadInputRef}>
                      <UploadInput
                        onUpload={(newFile, templateTypeId) => this.handleUploadClick(newFile, templateTypeId)}
                        allowedTypes={['image/jpg', 'image/jpeg', 'image/png']}
                        maxFileSize={75000000}
                        imageDimensions={{
                          width: 1000,
                          height: 1000,
                        }}
                        projectId={this.props.projectId}
                        autoSelectPrimaryTemplate={true}
                        inspectionId={this.props.inspectionId}
                        inspectionTemplateTypeId={this.props.inspectionTemplateTypeId}
                        showHelpText
                      />
                    </div>
                  </>
                ) : (
                  <div className='upload-site-maps-button'>
                    <UploadInput
                      onUpload={(newFile, templateTypeId) => this.handleUploadClick(newFile, templateTypeId)}
                      allowedTypes={['image/jpg', 'image/jpeg', 'image/png']}
                      maxFileSize={75000000}
                      imageDimensions={{
                        width: 1000,
                        height: 1000,
                      }}
                      projectId={this.props.projectId}
                      autoSelectPrimaryTemplate={true}
                      inspectionId={this.props.inspectionId}
                      inspectionTemplateTypeId={this.props.inspectionTemplateTypeId}
                      showHelpText
                    />
                  </div>
                )
              )}
          </>
        )}

        <ConfirmationModal
          show={this.state.showConfirmation}
          handleClose={() => this.setState({ showConfirmation: false })}
          handleConfirm={this.handleDelete}
          title='Are you sure you want to delete this site map?'
          subTitle='This can not be undone.'
          buttonText='Delete Site Map'
        />

        <ConfirmationModal
          show={this.state.showArchiveConfirmation}
          handleClose={() => this.setState({ showArchiveConfirmation: false })}
          handleConfirm={this.handleArchive}
          title='Are you sure you want to archive this site map?'
          subTitle='This will be only visible on the archive site map page'
          buttonText='Archive Site Map'
        />

        <ConfirmationModal
          show={this.state.showRestoreConfirmation}
          handleClose={() => this.setState({ showRestoreConfirmation: false })}
          handleConfirm={this.handleArchive}
          title='Are you sure you want to restore this site map?'
          buttonText='Restore Site Map'
        />

        <ConfirmationModal
          show={this.state.showAspectRatioWarning}
          handleClose={() => this.setState({
            showAspectRatioWarning: false,
            pendingReplaceData: null,
            aspectWarningMessage: '',
          })}
          handleConfirm={this.handleAspectRatioConfirm}
          title='Aspect Ratio Warning'
          subTitle={this.state.aspectWarningMessage}
          buttonText='Yes, Proceed'
        />

        {this.state.showPrintConfirmation && this.props.inspectionId > 0 && (
          <SiteMapPrint
            onClose={() => this.setState({ showPrintConfirmation: false })}
            targetDoc={this.state.targetDoc}
            projectLevelEsriEnabled={this.props.esriLayersEnabled}
            isGISEnabled={this.props.isGISEnabled}
          />
        )}

        {this.state.showPrintConfirmation && !this.props.inspectionId && (
          <ProjectPrintProvider>
            <PrintSiteMapsModal
              onClose={() => this.setState({ showPrintConfirmation: false })}
              targetDoc={this.state.targetDoc}
              isGISEnabled={this.props.isGISEnabled}
            />
          </ProjectPrintProvider>
        )}
        {this.state.showReorderModal && this.props.siteMaps.length ? (
          <Modal
            title='Organize Site Maps'
            show={true}
            handleClose={() => {
              this.setState({ showReorderModal: false });
            }}
            handleSubmit={async (x) => {
              await this.handleSubmitReorderProjectDocs();
              await this.props.onDocumentUpload();
              this.setState({ showReorderModal: false });
            }}
            submitBtnText='Save'
          >
            <DragDropContext onDragEnd={this.handleOnDragEnd}>
              <Droppable droppableId='documentGroup'>
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {this.state.tempReorderProjectDocs.length &&
                      this.state.tempReorderProjectDocs.map((ele, index) => {
                        return (
                          <Draggable
                            key={`${index}`}
                            draggableId={`${index}`}
                            index={index}
                          >
                            {(provide) => (
                              <div
                                ref={provide.innerRef}
                                {...provide.draggableProps}
                                {...provide.dragHandleProps}
                                style={{
                                  ...provide.draggableProps.style,
                                  left: 'auto !important',
                                  top: 'auto !important',
                                  marginBottom: '5px',
                                }}
                              >
                                <span className='document-group-reorder-modal__draggable-element'>
                                  <FontAwesomeIcon
                                    icon='grip-lines'
                                    size='sm'
                                    color='#0d2330'
                                  />
                                  <span className='document-group-reorder-modal__draggable-name'>
                                    {ele.name ? ele.name : ele}
                                  </span>
                                </span>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </Modal>
        ) : null}

        <FileRenameModal
          show={this.state.showRenameModal}
          file={{
            guid: this.state.targetDoc.GUID,
            name: this.state.targetDoc.name,
          }}
          onCancel={() => this.setState({ showRenameModal: false })}
          onSubmit={(fileName) => this.handleRename(fileName)}
        />

        <Modal
          title='Upload Site Map'
          show={this.state.showTemplateLinkModal}
          handleClose={() => this.setState({
            showTemplateLinkModal: false,
            selectedTemplateType: null,
            selectedMapType: 'image',
            imageOverlay: false,
            ...this.resetEsriModalState(),
          })}
          handleSubmit={() => {
            const hasNoMaps = !this.props.siteMaps || this.props.siteMaps.length === 0;
            const templateReady = hasNoMaps || !!this.state.selectedTemplateType;

            if (templateReady && this.state.selectedMapType) {
              this.handleTemplateLinkConfirm();
            }
          }}
          submitBtnText='Continue'
          disableSubmit={
            this.state.loadingTemplates
            || !this.state.selectedMapType
            || (!(!this.props.siteMaps || this.props.siteMaps.length === 0) && !this.state.selectedTemplateType)
            || (this.state.esriLayers && !this.state.esriVerified)
          }
        >
          {(() => {
            const hasNoMaps = !this.props.siteMaps || this.props.siteMaps.length === 0;

            return (

          <div style={{ padding: '20px' }}>
            <p style={{ marginBottom: '15px', fontSize: '16px' }}>
              Where would you like to link this upload with?
            </p>

            {this.state.loadingTemplates && !hasNoMaps ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <i className="fa fa-spinner fa-spin" style={{ fontSize: '24px' }} />
                <p>Loading templates...</p>
              </div>
            ) : hasNoMaps || this.state.inspectionTemplates.length > 0 ? (
              <div>
              {!hasNoMaps && (
                  <>
                    <label htmlFor="template-select" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                      Select Inspection Template:
                    </label>
                    <select
                      id="template-select"
                      className='pure-input-1'
                      value={this.state.selectedTemplateType !== null && this.state.selectedTemplateType !== undefined ? String(this.state.selectedTemplateType) : ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const numValue = value ? Number(value) : null;

                        this.setState({ selectedTemplateType: numValue });
                      }}
                      style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                    >
                      <option value="">-- Select a template --</option>
                      {this.state.inspectionTemplates.map((template) => (
                        <option
                          key={template.inspection_template.inspection_type_id}
                          value={String(template.inspection_template.inspection_type_id)}
                        >
                          {template.inspection_template.inspectionTemplateType?.name || template.inspection_template.name} {template.is_primary ? '(Primary)' : '(Secondary)'}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {this.props.primaryMappingMode === 'gis' && this.props.isGISEnabled && (
                  <div style={{ marginTop: '15px' }}>
                    <label htmlFor="map-type-select" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                      Select Map Type:
                    </label>
                    <select
                      id="map-type-select"
                      className='pure-input-1'
                      value={this.state.selectedMapType || ''}
                      onChange={(e) => {
                        const value = e.target.value || null;

                        this.setState({
                          selectedMapType: value,
                          imageOverlay: false,
                          esriLayers: false,
                        });
                      }}
                      style={{ width: '100%', padding: '8px', fontSize: '14px' }}
                    >
                      <option value="image">Upload Plans (Drawing)</option>
                      <option value="gis">GIS Mode (Live Map)</option>
                    </select>

                    {this.state.selectedMapType === 'gis' && (this.props.imageOverlayEnabled || this.props.esriLayersEnabled) && (
                      <div style={{ marginTop: '15px' }}>
                        {this.props.imageOverlayEnabled && (
                          <label htmlFor='image-overlay-checkbox' style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer' }}>
                            <input
                              type='checkbox'
                              id='image-overlay-checkbox'
                              checked={this.state.imageOverlay}
                              onChange={(e) => this.setState({ imageOverlay: e.target.checked })}
                              style={{ width: 'auto', margin: 0 }}
                            />
                            <span>Image Overlay</span>
                          </label>
                        )}
                        {this.props.esriLayersEnabled && (
                          <>
                            <label htmlFor='esri-layers-checkbox' style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                              <input
                                type='checkbox'
                                id='esri-layers-checkbox'
                                checked={this.state.esriLayers}
                                onChange={(e) =>
                                  this.setState({
                                    ...(e.target.checked
                                      ? { esriLayers: true }
                                      : this.resetEsriModalState()),
                                  })
                                }
                                style={{ width: 'auto', margin: 0 }}
                              />
                              <span>ESRI / ArcGIS Layers</span>
                            </label>

                            {this.state.esriLayers && (
                              <div style={{ marginTop: '10px', paddingLeft: '24px' }}>
                                <label htmlFor='esri-url-input' style={{ display: 'block', marginBottom: '4px', fontSize: '13px' }}>
                                  Layer URL
                                </label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                  <input
                                    type='url'
                                    id='esri-url-input'
                                    value={this.state.esriUrl}
                                    onChange={(e) =>
                                      this.setState({
                                        esriUrl: e.target.value,
                                        esriVerified: false,
                                        esriError: null,
                                      })
                                    }
                                    placeholder='https://.../FeatureServer/0'
                                    style={{ flex: 1, padding: '6px', fontSize: '13px' }}
                                  />
                                  <button
                                    type='button'
                                    onClick={this.handleEsriCheckUrl}
                                    disabled={
                                      this.state.esriChecking
                                      || !this.state.esriUrl.trim()
                                    }
                                    style={{ padding: '6px 12px', fontSize: '13px' }}
                                  >
                                    {this.state.esriChecking ? 'Checking...' : 'CHECK URL'}
                                  </button>
                                </div>
                                {this.state.esriVerified && (
                                  <div style={{ color: 'green', fontSize: '12px', marginTop: '4px' }}>
                                    Verified
                                  </div>
                                )}
                                {this.state.esriError && (
                                  <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                                    {this.state.esriError}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>No inspection templates found for this project.</p>
              </div>
            )}
          </div>
            );
          })()}
        </Modal>

        <SuccessModal
          show={this.state.error.length > 0}
          handleClose={() => this.setState({ error: '' })}
          handleSubmit={() => this.setState({ error: '' })}
          submitBtnText='OK'
          message={this.state.error}
          title='Error replacing document'
          isAlert
        />
      </Fragment>
    );
  }
}

SiteMapsUI.propTypes = {
  /**
   * Uploads
   */
  siteMaps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      GUID: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
    }).isRequired,
  ).isRequired,
  archivesiteMaps: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      GUID: PropTypes.string.isRequired,
      id: PropTypes.number.isRequired,
    }).isRequired,
  ).isRequired,
  projectId: PropTypes.number.isRequired,
  projectName: PropTypes.string.isRequired,
  onDocumentDelete: PropTypes.func,
  onDocumentUpload: PropTypes.func,
  showActions: PropTypes.bool,
  inspectionId: PropTypes.number,
  inspectionTemplateTypeId: PropTypes.number,
  isActiveMap: PropTypes.bool,
  permCheck: PropTypes.func,
  docTypes: PropTypes.arrayOf(
    PropTypes.shape({
      project_doc_type_id: PropTypes.number,
      inspection_template_type_id: PropTypes.number,
      inspection_template_type: PropTypes.string,
      is_primary: PropTypes.bool,
    })
  ),
  primaryMappingMode: PropTypes.string,
  imageOverlayEnabled: PropTypes.bool,
  esriLayersEnabled: PropTypes.bool,
};

SiteMapsUI.defaultProps = {
  inspectionId: 0,
};

export const SiteMaps = ({ ...props }) => {
  const history = useHistory();
  const histUrlString = history.location.pathname;
  const urlReg = RegExp('active');
  const checkRegex = urlReg.test(histUrlString);
  const rolesContext = useContext(RolesContext);
  const projectContext = useContext(ProjectContext);
  const projectPermissionContext = useContext(ProjectPermissionContext);
  const inspectionContext = useContext(require('../../../contexts/InspectionContext').default);
  const featureContext = useContext(TenantFeatureContext);
  const docTypes = projectContext.projectDocs.documentTypes.filter(
    (dType) => dType.name === 'Active Site Maps',
  );

  const permCheck = rolesContext.userHasPermission;
  // const docs = docTypes.length ? docTypes.map(ele => ele.documents) : [];
  const docs = docTypes.length ? docTypes[0].documents : [];

  // Filter docs based on inspection context if accessed via inspection
  const filteredDocs = React.useMemo(() => {
    if (props.inspectionId && inspectionContext?.inspection?.inspection_template) {
      // When accessed via inspection, filter by matching inspection_template_type_id
      return docs.filter((doc) =>
        doc.inspection_template_type_id === inspectionContext.inspection.inspection_template.inspection_type_id
      );
    }
    // When accessed via project active site maps, show all docs

    return docs;
  }, [docs, props.inspectionId, inspectionContext?.inspection?.template_id]);

  const [siteMaps, setSiteMaps] = useState(filteredDocs.filter((doc) => (isSupportedMimeType(doc.mime_type) || doc.ProjectUploadModel?.upload_id == null) && !doc.archived_at))
  const [archivesiteMaps, setArchiveSiteMaps] = useState(filteredDocs.filter((doc) => (isSupportedMimeType(doc.mime_type) || doc.ProjectUploadModel?.upload_id == null) && doc.archived_at))
  const auth = useContext(AppContext).get('user');

  const { search } = useResource({ resource: 'divisionProjects' });
  const { query, setQuery } = search;

  const searchProps = {
    query,
    setQuery(input) {
      setQuery(input);
    },
    placeholder: 'Search Site Maps..',
  };

  useEffect(() => {
    setSiteMaps(filteredDocs.filter((doc) => (isSupportedMimeType(doc.mime_type) || doc.ProjectUploadModel?.upload_id == null) && !doc.archived_at))
    setArchiveSiteMaps(filteredDocs.filter((doc) => (isSupportedMimeType(doc.mime_type) || doc.ProjectUploadModel?.upload_id == null) && doc.archived_at))
  }, [filteredDocs]);

  // Create docTypes array with project_doc_type_id, inspection_template_type_id, inspection_template_type, and is_primary
  const docTypesArray = filteredDocs.map(doc => ({
    project_doc_type_id: doc.ProjectUploadModel.project_doc_type_id,
    inspection_template_type_id: doc.inspection_template_type_id,
    inspection_template_type: doc.inspection_template_type,
    is_primary: doc.is_primary,
  }));

  // Remove duplicates based on inspection_template_type_id
  const uniqueDocTypes = docTypesArray.filter((docType, index, self) =>
    index === self.findIndex((t) => t.inspection_template_type_id === docType.inspection_template_type_id)
  );

  // Sort so primary templates come first
  uniqueDocTypes.sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;

    return 0;
  });

  return (
    <SiteMapsUI
      {...props}
      history={history}
      siteMaps={siteMaps}
      archivesiteMaps={archivesiteMaps}
      projectId={projectContext.project.id}
      projectName={projectContext.project.name}
      projectPermissionContext={projectPermissionContext}
      isActiveMap={checkRegex}
      roleName={auth.roleName}
      searchProps={searchProps}
      searchValue={search}
      setSiteMaps={setSiteMaps}
      setArchiveSiteMaps={setArchiveSiteMaps}
      permCheck={permCheck}
      isDownloaded={projectContext.isDownloaded}
      docTypes={uniqueDocTypes}
      primaryMappingMode={projectContext.project?.primary_mapping_mode || 'image'}
      imageOverlayEnabled={!!projectContext.project?.image_overlay_enabled}
      esriLayersEnabled={!!projectContext.project?.esri_layers_enabled}
      isGISEnabled={featureContext.tenantHasFeature('GIS Configuration')}
    />
  );
};

export default SiteMaps;
